import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useSeo } from "@/hooks/usePublicData";

/**
 * Client-side metadata for single-page navigations.
 *
 * The server already injects the correct tags into the initial HTML — that is
 * what link unfurlers and crawlers read, and it is the authoritative source.
 * This component exists for what happens *after* that: clicking through the site
 * produces no new document, so without it the tab title and canonical URL would
 * stay frozen on whichever page the visitor first landed on.
 *
 * It deliberately updates title, description and canonical only. Open Graph tags
 * are pointless to update client-side (no unfurler executes JavaScript), and
 * rewriting them would only create a discrepancy with what the server sent.
 */

/** Track the tags we created, so navigating away removes them cleanly. */
function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement(attributes.tag || "meta");
    element.setAttribute("data-spa-managed", "true");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    if (key === "tag") return;
    if (value === null || value === undefined || value === "") element.removeAttribute(key);
    else element.setAttribute(key, value);
  });
  return element;
}

export default function SeoHead({ pageKey, title, description, noindex }) {
  const location = useLocation();
  const { data } = useSeo(pageKey);

  const seo = data ? data.seo : null;

  const effectiveTitle = title || (seo && seo.title) || null;
  const effectiveDescription = description || (seo && seo.description) || null;

  useEffect(() => {
    if (effectiveTitle) document.title = effectiveTitle;
  }, [effectiveTitle]);

  useEffect(() => {
    if (effectiveDescription) {
      upsertMeta('meta[name="description"]', { name: "description", content: effectiveDescription });
    }
  }, [effectiveDescription]);

  useEffect(() => {
    const href = `${window.location.origin}${location.pathname}`;
    upsertMeta('link[rel="canonical"]', { tag: "link", rel: "canonical", href });
  }, [location.pathname]);

  useEffect(() => {
    // Applies to preview and draft views, which must never be indexed even if
    // their URL leaks.
    if (noindex) {
      upsertMeta('meta[name="robots"]', { name: "robots", content: "noindex, nofollow" });
    }
  }, [noindex]);

  return null;
}
